/**
 * Services Slider Module
 * Ported from the Catalyst 2025 "Services Slider" block.
 *
 *  - page   mode: GSAP ScrollTrigger pins the section and converts the vertical
 *                 page scroll into a horizontal scroll of the services row.
 *  - arrows mode: native horizontal scroll track. Hovering an arrow eases the
 *                 track left/right, leaving eases it to a stop; click/keyboard
 *                 pages the track; arrows auto-hide at each end.
 *  - static mode: no JS.
 */
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/* -------------------------------------------------------------------------- */
/*  Page-scroll mode                                                           */
/* -------------------------------------------------------------------------- */
function initPageScroll(block) {
  const viewport = block.querySelector('.services-slider');
  const inner = block.querySelector('.services-slider-track');
  if (!viewport || !inner) return;

  // How far the track must travel for its last card to land on the viewport's
  // inner edge (the container edge, or the screen edge on a breakout side).
  const distance = () => inner.scrollWidth - viewport.clientWidth;

  // Only pin + scroll when the row actually overflows the viewport, otherwise
  // the negative distance would shove the row sideways.
  if (distance() <= 0) return;

  gsap.to(inner, {
    x: () => -distance(),
    ease: 'none',
    scrollTrigger: {
      trigger: block,
      pin: true,
      scrub: true,
      start: 'center center',
      end: () => '+=' + distance(),
      anticipatePin: 1,
      invalidateOnRefresh: true,
      pinSpacing: true,
    },
  });

  // If the page loads already scrolled past the slider, jump to the end for continuity.
  if (window.scrollY > block.offsetTop + block.offsetHeight) {
    gsap.set(inner, { x: -distance() });
  }
}

/* -------------------------------------------------------------------------- */
/*  Arrows mode                                                                */
/* -------------------------------------------------------------------------- */
function initArrows(block) {
  const track = block.querySelector('.services-scroll');
  const prev = block.querySelector('.services-arrow.-prev');
  const next = block.querySelector('.services-arrow.-next');
  if (!track || !prev || !next) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const MAX = 30; // peak scroll speed, px per frame
  const EASE = 0.12; // approach factor toward the target speed
  let vel = 0;
  let target = 0;
  let raf = null;

  const maxScroll = () => track.scrollWidth - track.clientWidth;

  function updateArrows() {
    prev.classList.toggle('-disabled', track.scrollLeft <= 1);
    next.classList.toggle('-disabled', track.scrollLeft >= maxScroll() - 1);
  }

  function frame() {
    vel += (target - vel) * EASE;
    track.scrollLeft += vel;
    // Stop cleanly at either edge.
    if ((vel < 0 && track.scrollLeft <= 0) || (vel > 0 && track.scrollLeft >= maxScroll())) {
      vel = 0;
      target = 0;
    }
    updateArrows();
    if (target === 0 && Math.abs(vel) < 0.1) {
      vel = 0;
      raf = null;
      return;
    }
    raf = requestAnimationFrame(frame);
  }

  function page(dir) {
    track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: 'smooth' });
  }

  function start(dir) {
    if (reduce) {
      page(dir);
      return;
    }
    target = dir * MAX;
    if (!raf) raf = requestAnimationFrame(frame);
  }

  function stop() {
    target = 0;
  }

  prev.addEventListener('mouseenter', () => start(-1));
  next.addEventListener('mouseenter', () => start(1));
  prev.addEventListener('mouseleave', stop);
  next.addEventListener('mouseleave', stop);

  // Click / keyboard activation pages the track (covers touch + a11y).
  prev.addEventListener('click', () => page(-1));
  next.addEventListener('click', () => page(1));

  track.addEventListener('scroll', updateArrows, { passive: true });
  window.addEventListener('resize', updateArrows);
  updateArrows();
}

/* -------------------------------------------------------------------------- */
/*  Init                                                                       */
/* -------------------------------------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  const blocks = document.querySelectorAll('.services-slider-module');
  if (!blocks.length) return;

  let hasPageScroll = false;

  blocks.forEach((block) => {
    const mode = block.dataset.scrollMode;
    if (mode === 'page') {
      initPageScroll(block);
      hasPageScroll = true;
    } else if (mode === 'arrows') {
      initArrows(block);
    }
  });

  if (hasPageScroll) {
    window.addEventListener('resize', () => ScrollTrigger.refresh());
  }
});
